import { useCallback, useMemo, useState } from 'react'
import { CurrentStep, StepHandle, UntilStep } from './interfaces'

type setStepCallbackType = (step: number | ((step: number) => number)) => void

export const useStep = (
    maxStep: number,
    linear?: boolean
): [CurrentStep, StepHandle, UntilStep] => {
    const [currentStep, setCurrentStep] = useState(1)
    const [untilStep, setUntilStep] = useState(1)

    const canGoToNextStep = useMemo(
        () => currentStep + 1 <= maxStep,
        [currentStep, maxStep]
    )

    const canGoToPrevStep = useMemo(() => currentStep - 1 >= 1, [currentStep])

    const canGoToSetStep = useCallback(
        (step: number) => !!linear && untilStep >= step,
        [currentStep]
    )

    const setStep = useCallback<setStepCallbackType>(
        step => {
            const newStep = step instanceof Function ? step(currentStep) : step

            if (newStep >= 1 && newStep <= maxStep) {
                if (!canGoToSetStep(newStep)) return
                setCurrentStep(newStep)
                setUntilStep(oldStep => {
                    return newStep >= untilStep ? newStep : oldStep
                })
                return
            }

            throw new Error(`Step ${step} not valid`)
        },
        [maxStep, currentStep]
    )

    const goToNextStep = useCallback(() => {
        if (canGoToNextStep) {
            setCurrentStep(step => step + 1)
            setUntilStep(oldStep => {
                const until = currentStep + 1
                return until <= oldStep ? oldStep : until
            })
        }
    }, [canGoToNextStep, currentStep])

    const goToPrevStep = useCallback(() => {
        if (canGoToPrevStep) {
            setCurrentStep(step => step - 1)
        }
    }, [canGoToPrevStep])

    const reset = useCallback(() => {
        setCurrentStep(1)
        setUntilStep(1)
    }, [])

    const getCurrent = useCallback(() => currentStep, [currentStep])
    const getUntil = () => untilStep

    return [
        currentStep,
        {
            goToNextStep,
            goToPrevStep,
            getCurrent,
            getUntil,
            canGoToNextStep,
            canGoToPrevStep,
            canGoToSetStep,
            setStep,
            reset
        },
        untilStep
    ]
}
